import {
	AfterViewInit,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { StoredSeance } from "apps/idl/src/anakin/models/StoredSeance";
import { DateHelper } from "apps/idl/src/helpers";
import { ETimetablePattern } from "apps/idl/src/model";
import { Acte } from "apps/idl/src/model/Acte";
import { EDuree } from "apps/idl/src/model/EDuree";
import { ERepetition } from "apps/idl/src/model/ERepetition";
import { C_HOURS_MINUTES_REPETITION_TYPE } from "apps/idl/src/modules/event-markers/models/hours-minutes-repetition";
import { Recurrence } from "apps/idl/src/modules/event-markers/models/recurrence";
import { EEtatActe } from "../../enums/EEtatActe";
import { ETaux } from "../../enums/ETaux";
import { PopoverService } from "../../services/popover.service";
import { SeanceService } from "../../services/seance.service";
import { InformationAbattementComponent } from "../information-abattement/information-abattement.component";
import { InformationPopoverComponent } from "../information-popover/information-popover.component";
import { EEtatRegle } from "apps/idl/src/model/EEtatRegle";
import { ACT_IDS } from "apps/idl/src/anakin/shared/acts.constants";

@Component({
	selector: "di-acte",
	templateUrl: "./acte.component.html",
	styleUrls: ["./acte.component.scss"],
})
export class ActeComponent implements OnInit, OnChanges, AfterViewInit {
	@Input() acte: Acte;
	@Input() ellipsis = false;
	@Input() favoriteIcon = false;
	@Input() clickable = false;
	@Input() priseEnCharge = false;
	@Input() noCheckForce = false;
	@Input() status?: EEtatActe;
	@Input() clickablePrice: boolean = false;
	@Input() checkboxClickable: boolean = true;
	@Input() changeTauxActe: (acte: Acte,isAuto:boolean) => void;
	@Input() patientDependant?: boolean;
	@Input() seanceRealisee?: boolean;
	@Input() seanceDependance?: boolean;
	@Input() acteErreurMessage?: string;
	@Output() onCheckboxChange = new EventEmitter<boolean>();
	@Output() onActeSelected = new EventEmitter<Acte>();
	@ViewChild("descriptionElement") descriptionElement!: ElementRef;

	public infoSecondaire = "";
	public isMultiLine = false;
	public classes: any;
	public libelleActe = "";

	constructor(
		private svcPopover: PopoverService
	){}

	ngOnInit() {
		if (this.priseEnCharge) {
			this.getInfoSecondaire();
		}
		this.updateClasses();
		this.libelleActe = (this.acte as Acte).getDescription(this.patientDependant, this.seanceRealisee, this.seanceDependance);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['patientDependant'] || changes['seanceRealisee'] || changes['seanceDependance']) {
			this.libelleActe = (this.acte as Acte).getDescription(this.patientDependant, this.seanceRealisee, this.seanceDependance);
		}
	}

	ngAfterViewInit() {
		const element = this.descriptionElement.nativeElement;
		this.isMultiLine = element.scrollHeight > 50;
	}

	private updateClasses() {
		this.status = this.acte.etat || EEtatActe.to_be_done;
		this.classes = {
			"multi-line": this.isMultiLine,
			"single-line": !this.isMultiLine,
			ellipsis: this.ellipsis,
			done: this.status === EEtatActe.done,
			"not-done": this.status === EEtatActe.not_done,
			"to-be-done": this.status === EEtatActe.to_be_done,
			clickable: this.clickable,
			wthCheck: this.status || this.favoriteIcon || !this.noCheckForce
		};
	}

	getClasses() {
		return this.classes;
	}

	onSelectActe(acte: Acte) {
		this.onActeSelected.emit(acte);
	}

	handleCheckboxChange(checked: boolean) {
		this.onCheckboxChange.emit(checked);
	}

	getInfoSecondaire() {
		let recurrence: Recurrence = this.acte.recurrences[0] as Recurrence
		if (!recurrence) {
			this.infoSecondaire = "";
			return;
		}
		
		let moments: string[] = [];
		const recurrenceSort = SeanceService.sortDayRepetitions(recurrence.dayRepetitions);
		recurrenceSort.forEach((repetition) => {
			if(repetition.type === C_HOURS_MINUTES_REPETITION_TYPE)
			{
				const hours = repetition.hours.toString().padStart(2, '0');
				const minutes = repetition.minutes.toString().padStart(2, '0');
				moments.push(`${hours}:${minutes}`);
			}
			else
			{
				moments.push(StoredSeance.determineMoment(repetition));
			}
		});

		let repetitionJours: string[] = [];
		if (recurrence.repetitionType === ERepetition.jours) {
			const joursSemaine = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
			repetitionJours = Array.isArray(recurrence.repetitionValue) 
			? recurrence.repetitionValue.map(jrs => joursSemaine[jrs])
			: [];
		}else{
			if (recurrence.repetitionType === ERepetition.tsLesXJours && typeof recurrence.repetitionValue === 'number') {
				repetitionJours.push(`tous les ${recurrence.repetitionValue === 1 ? "" : recurrence.repetitionValue } jours`)
			}
		}
		
		let repeterString: string;
		try{
			if (recurrence.durationType === EDuree.jusquADate && Array.isArray(recurrence.durationValue) && DateHelper.isDate(recurrence.durationValue[0])) {
				repeterString = `jusqu'au ${DateHelper.transform(recurrence.durationValue[0], ETimetablePattern.dd_MM_yyyy_slash)}`
			} else {
				if (recurrence.durationType === EDuree.dates && Array.isArray(recurrence.durationValue)) {
					let dates = recurrence.durationValue as Date[];
					repeterString = 'Occurrences : ';
					for (let date of dates) {
						repeterString += ' ' + DateHelper.transform(date, ETimetablePattern.dd_MM_yyyy_slash) + ','
					}
					repeterString = repeterString.slice(0, -1);
				} else {
					repeterString = `pendant ${recurrence.durationValue} ${recurrence.durationType}`
				}
			}
		} catch (error) {
			repeterString = '';
		}
		
		const momentsString = moments.join(", ");
		const repetitionSemaineString: string = `toutes les ${this.acte.weekRepetition.value || ''} semaines`;
		const repetitionJoursString = repetitionJours.join(", ");
		const dateDebut = `À partir du ${DateHelper.transform(this.acte.startDate, ETimetablePattern.dd_MM_yyyy_slash)}`

		// Concaténation du tout avec " - " entre chaque section
		this.infoSecondaire = [dateDebut,momentsString, repetitionJoursString, repetitionSemaineString, repeterString]
			.filter(Boolean) // Evite les segments vides
			.join(" - ");
	}

	public showInfoAbattement(event: Event) {
		this.svcPopover.open(
			"",
			event.currentTarget,
			InformationAbattementComponent,
			{
				acte: this.acte,
				seanceDependance: this.seanceDependance,
				close: () => {
					this.svcPopover.close();
				},
				changeTaux: (taux: any,isAuto:boolean) => { 
					if(taux === ETaux.AUTO)
					{
						this.acte.isAbattementForced = false;
						delete this.acte.taxAllowanceForced
					}
					else
					{
						this.acte.isAbattementForced = true;
						if(taux === ETaux.GRATUIT)
							this.acte.taxAllowanceForced = 0;
						else
							this.acte.taxAllowanceForced = Number(taux);
					}
					if(this.acte instanceof Acte)
						this.acte.calculatePrice()
					this.changeTauxActe(this.acte,isAuto);
					this.svcPopover.close();
        		},
				// L'abattement manuel est désactivé pour l'IFI 0.01
				disableAbattementManuel: this.acte._id === ACT_IDS.IFI_0p01
			}
		)
	}

	public showErreurActe(event: Event) {
		this.svcPopover.open(
			"",
			event.currentTarget,
			InformationPopoverComponent,
			{
				regles: [
					{
						description: this.acteErreurMessage,
						etat: EEtatRegle.erreur
					}
				],
				close: () => {
					this.svcPopover.close();
				},
			}
		)
	}
}
